// frontend/src/components/Common/AuditLog/AuditLogPanel.jsx

import React, { useState, useEffect } from 'react';
import { auditLogAPI } from '../../../api/client';
import { useTranslation } from '../../../context/TranslationContext';
import { FiX, FiClock, FiUser, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import './AuditLog.css';

const AuditLogPanel = ({ isOpen, onClose, entityType, entityId, title }) => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [details, setDetails] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadAuditLogs();
    }
  }, [isOpen, entityType, entityId]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await auditLogAPI.getAuditLogs({
        entityType,
        entityId,
        limit: 100
      });

      if (response.data.success) {
        setLogs(response.data.data);
      }
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (auditLogId) => {
    if (details[auditLogId]) {
      // Already loaded, just toggle
      setExpandedLog(expandedLog === auditLogId ? null : auditLogId);
      return;
    }

    try {
      const response = await auditLogAPI.getAuditLogDetails(auditLogId);
      if (response.data.success) {
        setDetails(prev => ({
          ...prev,
          [auditLogId]: response.data.details
        }));
        setExpandedLog(auditLogId);
      }
    } catch (error) {
      console.error('Error loading audit log details:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getOperationColor = (operation) => {
    switch (operation) {
      case 'INSERT': return '#10b981';
      case 'MODIFY': return '#f59e0b';
      case 'DELETE': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getOperationLabel = (operation) => {
    const labels = {
      INSERT: t('audit.insert', 'INSERT'),
      MODIFY: t('audit.modify', 'MODIFY'),
      DELETE: t('audit.delete', 'DELETE')
    };
    return labels[operation] || operation;
  };

  const formatDescription = (log) => {
    if (log.entity_type === 'sample_order_line') {
      // 'Line 1-1 (S) modified - ...' → 'Line 1'
      const m = log.description?.match(/[Ll]ine\s+(\d+)/);
      return m ? `Line ${m[1]}` : log.description;
    }
    return log.description || `${log.operation_type} — ${log.entity_type}`;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="audit-log-overlay" onClick={onClose} />

      {/* Panel */}
      <div className="audit-log-panel">
        {/* Header */}
        <div className="audit-log-header">
          <div className="audit-log-header-title">
            <FiClock size={20} />
            <h3>{title || t('audit.title', 'Change History')}</h3>
          </div>
          <button onClick={onClose} className="btn-close">
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="audit-log-content">
          {loading ? (
            <div className="audit-log-loading">
              {t('loading', 'Loading...')}
            </div>
          ) : logs.length === 0 ? (
            <div className="audit-log-empty">
              <FiClock size={48} />
              <p>{t('audit.no_changes', 'No changes recorded')}</p>
            </div>
          ) : (
            <div className="audit-log-list">
              {logs.map(log => (
                <div key={log.audit_log_id} className="audit-log-item">
                  {/* Header Row */}
                  <div 
                    className="audit-log-item-header"
                    onClick={() => log.changes_count > 0 && loadDetails(log.audit_log_id)}
                    style={{ cursor: log.changes_count > 0 ? 'pointer' : 'default' }}
                  >
                    <div className="audit-log-item-left">
                      {log.entity_type === 'sample_order_line' && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, background: '#e0f2f1', color: 'var(--primary)',
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>
                          LINHA
                        </span>
                      )}
                      {log.entity_type === 'line_operation' && (
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 6px',
                          borderRadius: 4, background: '#fef3c7', color: '#92400e',
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>
                          OPERAÇÃO
                        </span>
                      )}
                      {log.changes_count > 0 && (
                        <span className="audit-log-expand-icon">
                          {expandedLog === log.audit_log_id ? 
                            <FiChevronDown size={16} /> : 
                            <FiChevronRight size={16} />
                          }
                        </span>
                      )}
                      <span 
                        className="audit-log-operation"
                        style={{ 
                          background: getOperationColor(log.operation_type),
                          color: 'white'
                        }}
                      >
                        {getOperationLabel(log.operation_type)}
                      </span>
                      <span className="audit-log-description">
                        {formatDescription(log)}
                      </span>
                    </div>
                    <div className="audit-log-item-right">
                      <span className="audit-log-user">
                        <FiUser size={14} /> {log.username}
                      </span>
                      <span className="audit-log-date">
                        {formatDate(log.changed_at)}
                      </span>
                    </div>
                  </div>

                  {/* Details (Expanded) */}
                  {expandedLog === log.audit_log_id && details[log.audit_log_id] && (
                    <div className="audit-log-details">
                      <table className="audit-log-details-table">
                        <thead>
                          <tr>
                            <th>{t('audit.field', 'Field')}</th>
                            <th>{t('audit.old_value', 'Old Value')}</th>
                            <th>{t('audit.new_value', 'New Value')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {details[log.audit_log_id].map(detail => (
                            <tr key={detail.audit_log_detail_id}>
                              <td className="audit-field-name">
                                {detail.field_label || detail.field_name}
                              </td>
                              <td className="audit-old-value">
                                {detail.old_value || <em>{t('audit.empty', '(empty)')}</em>}
                              </td>
                              <td className="audit-new-value">
                                {detail.new_value || <em>{t('audit.empty', '(empty)')}</em>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AuditLogPanel;